import React, { useState } from 'react';
import theme from './BudgetList.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BudgetGroupDto, BudgetGroupType, BudgetItemPriceType } from '@/types';
import { BuyerRoomDto } from '@/services/rooms';
import BudgetListRow from './BudgetListRow';
import ItemCategoryDisplay from '@/components/item-categories/ItemCategoryDisplay';
import { faChevronUp, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { ExpandableCard } from '@/components/Cards';
import BudgetTable from './BudgetTable';
import { useIsMobile } from '@/utils/hooks';
import { classnames } from '@/utils/html';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';

type Props = {
    data: BudgetGroupDto;
    groupedByRoom: boolean;
    roomMap: Map<number, BuyerRoomDto> | null;
};

const BudgetListItem: React.FC<Props> = ({ data, roomMap, groupedByRoom }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isMobile = useIsMobile();
    const renderName = () => {
        switch (data.type) {
            case BudgetGroupType.Normal: {
                if (groupedByRoom) {
                    const room = data.roomTagId ? roomMap?.get(data.roomTagId) : null;
                    return (
                        <>
                            {room?.icon && <FontAwesomeIcon icon={['fal', room.icon]} className={theme.roomIcon} />}
                            {room?.name ?? <Trans>General</Trans>}
                        </>
                    );
                }
                return <ItemCategoryDisplay value={data.categoryId} />;
            }
            case BudgetGroupType.ChangeRequests:
                return <Trans>Change requests</Trans>;
            case BudgetGroupType.ExtraCosts:
                return <Trans>Extra costs</Trans>;
            case BudgetGroupType.Presets:
                return <Trans>Styles</Trans>;
            case BudgetGroupType.Other:
                return <Trans>Other</Trans>;
            default:
                return <Trans>Unknown</Trans>;
        }
    };

    return (
        <ExpandableCard
            type="inside"
            expanded={isExpanded}
            className={classnames(theme.itemCard, { [theme.expanded]: isExpanded })}
            expandableContent={
                <div className={theme.expandWrapper}>
                    {isMobile ? (
                        <>
                            {data.items.map((i, index) => (
                                <BudgetListRow type={data.type} key={i.buyerOptionGroupId + '_' + index} budgetItem={i} />
                            ))}
                        </>
                    ) : (
                        <BudgetTable type={data.type} data={data.items} />
                    )}
                </div>
            }>
            <div onClick={() => setIsExpanded(!isExpanded)} className={theme.header}>
                <div className={theme.title}>
                    <div>{renderName()}</div>
                    {data.items.some((x) => x.priceType === BudgetItemPriceType.Unsigned) && (
                        <div className={theme.unsigned}>
                            <Trans>Contains unsigned options</Trans>
                        </div>
                    )}
                </div>
                {data.totalPrice !== null && (
                    <div className={theme.groupPrice}>
                        <BuyerCurrencyDisplay value={data.totalPrice} />
                    </div>
                )}
                <FontAwesomeIcon className={theme.icon} icon={isExpanded ? faChevronUp : faChevronDown} />
            </div>
        </ExpandableCard>
    );
};

export default BudgetListItem;
